import { api } from '../console/api.ts';
import { empty, pageHead } from '../console/components.ts';
import { esc, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { administersNubi } from '../console/session.ts';
import { dateControl, selectControl } from '../controls.ts';
import type { NubiPerson } from './administradores.ts';
import {
  ACTION,
  type AuditEntry,
  changeText,
  PLATFORM_AGENCY,
  type PlatformAgency,
  who,
} from './agencias.ts';

/**
 * «Administración de Nubi › Bitácora» (#274, ADR-0026): the whole administration log — every
 * change to the agencies, their carriers and the people of Nubi — newest first, filtered by the
 * person who made it, the agency it is about (or Nubi itself) and dates (Guatemala's, both
 * included). The per-agency log inside «Agencias» stays and links here.
 */

type Filters = { actor: string; agency: string; from: string; to: string };
const NONE: Filters = { actor: '', agency: '', from: '', to: '' };
let filters: Filters = { ...NONE };

/** Back to every entry (a new session, or the menu). */
export function resetBitacora() {
  filters = { ...NONE };
}

/** The filters as the API takes them (only the ones set). */
export function auditQuery(f: Filters = filters) {
  const q = new URLSearchParams();
  if (f.actor) q.set('actor', f.actor);
  if (f.agency) q.set('agency', f.agency);
  if (f.from) q.set('from', f.from);
  if (f.to) q.set('to', f.to);
  const s = q.toString();
  return `/v1/platform/audit${s ? `?${s}` : ''}`;
}

export async function bitacora() {
  const description =
    'Quién cambió qué y cuándo en la administración de Nubi: las agencias, sus aseguradoras y las personas de Nubi.';
  if (!administersNubi())
    return `${pageHead('Bitácora', description)}${empty('lock', 'Esta sección es de la administración de Nubi', 'Tu rol no lee la bitácora de la plataforma.', '', 2)}`;
  const [entries, agencies, people] = await Promise.all([
    api<AuditEntry[]>('GET', auditQuery()),
    api<PlatformAgency[]>('GET', '/v1/platform/agencies'),
    api<NubiPerson[]>('GET', '/v1/platform/admins').catch(() => [] as NubiPerson[]),
  ]);
  const agencyName = (id: string) =>
    id === PLATFORM_AGENCY ? 'Nubi (plataforma)' : (agencies.find((a) => a.id === id)?.name ?? id);
  // The people of Nubi now, and anyone the current filter names (a person who left keeps entries).
  const emails = [...new Set([...people.map((p) => p.email), filters.actor].filter(Boolean))];
  const personFilter = selectControl({ id: 'audit-actor', label: 'Persona' }, [
    { value: '', label: 'Todas las personas', selected: filters.actor === '' },
    ...emails.map((e) => ({ value: e, label: e, selected: e === filters.actor })),
  ]);
  const agencyFilter = selectControl({ id: 'audit-filter-agency', label: 'Agencia' }, [
    { value: '', label: 'Todas', selected: filters.agency === '' },
    {
      value: PLATFORM_AGENCY,
      label: 'Nubi (plataforma)',
      selected: filters.agency === PLATFORM_AGENCY,
    },
    ...agencies.map((a) => ({ value: a.id, label: a.name, selected: a.id === filters.agency })),
  ]);
  const filtered = Object.values(filters).some(Boolean);
  const toolbar = `<div class="toolbar audit-filters" role="group" aria-label="Filtros de la bitácora">
    <div class="field">${personFilter}</div>
    <div class="field">${agencyFilter}</div>
    <div class="field">${dateControl({ id: 'audit-from', label: 'Desde', value: filters.from })}</div>
    <div class="field">${dateControl({ id: 'audit-to', label: 'Hasta', value: filters.to })}</div>
    ${filtered ? '<button type="button" class="btn" data-audit-clear>Quitar filtros</button>' : ''}
  </div>`;
  const rows = entries
    .map(
      (e) => `<tr><td class="nowrap">${esc(when(e.at))}</td><td>${esc(who(e))}</td>
        <td>${esc(agencyName(e.agencyId))}</td><td>${esc(ACTION[e.action] ?? e.action)}</td>
        <td>${esc(changeText(e))}</td></tr>`,
    )
    .join('');
  const body = entries.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Bitácora de la administración de Nubi, lo más reciente primero</caption>
      <thead><tr><th scope="col">Cuándo</th><th scope="col">Quién</th><th scope="col">Sobre</th><th scope="col">Qué hizo</th><th scope="col">Antes → después</th></tr></thead>
      <tbody>${rows}</tbody></table></div>`
    : `<p class="muted small" style="padding:0 var(--s-5) var(--s-5)">${filtered ? 'Ningún cambio cumple estos filtros.' : 'Todavía no hay cambios.'}</p>`;
  return `${pageHead('Bitácora', description)}<section class="card" data-component="platform-audit-log"><div class="card-head"><h2>Cambios</h2><span class="spacer"></span><span class="muted small" aria-live="polite">${entries.length} ${entries.length === 1 ? 'cambio' : 'cambios'}${entries.length >= 200 ? ' (los más recientes)' : ''}</span></div>
    ${toolbar}${body}</section>`;
}

async function repaint(focus?: string) {
  await render(current, { quiet: true });
  if (focus) document.querySelector<HTMLElement>(focus)?.focus();
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** A date filter: a whole day (anything else clears it), «Desde» never after «Hasta» (they swap). */
function setDay(key: 'from' | 'to', value: string) {
  filters = { ...filters, [key]: DAY.test(value) ? value : '' };
  if (filters.from && filters.to && filters.from > filters.to)
    filters = { ...filters, from: filters.to, to: filters.from };
}

export const actions: Actions = {
  click: {
    auditClear: () => {
      filters = { ...NONE };
      void repaint('#audit-actor');
    },
  },
  change: {
    'audit-actor': (control) => {
      filters = { ...filters, actor: control.value };
      void repaint('#audit-actor');
    },
    'audit-filter-agency': (control) => {
      filters = { ...filters, agency: control.value };
      void repaint('#audit-filter-agency');
    },
    'audit-from': (control) => {
      setDay('from', control.value);
      void repaint('#audit-from');
    },
    'audit-to': (control) => {
      setDay('to', control.value);
      void repaint('#audit-to');
    },
  },
};
